import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getTicketTransferOptions, getTicketTransfers, transferTicket } from './api';
import { ticketTransferKeys } from './keys';
import { TransferTicketBody } from './types';
import { groupTicketKeys } from '@/features/groupTicket/keys';
import { publicMemberKeys } from '@/features/publicMember/keys';

export const useTicketTransferOptions = ({
    centerId,
    memberId,
    enabled = true,
}: {
    centerId: string;
    memberId: string;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: ticketTransferKeys.options(centerId, memberId),
        queryFn: () => getTicketTransferOptions({ centerId, memberId }),
        enabled: enabled && !!centerId && !!memberId,
        select: (data) => data?.data?.results ?? [],
    });

export const useTicketTransfers = ({
    centerId,
    params,
    enabled = true,
}: {
    centerId: string;
    params?: Record<string, string>;
    enabled?: boolean;
}) =>
    useQuery({
        queryKey: [...ticketTransferKeys.list(centerId), params],
        queryFn: () => getTicketTransfers({ centerId, params }),
        enabled: enabled && !!centerId,
        select: (data) => data?.data ?? null,
    });

export const useTransferTicket = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ centerId, body }: { centerId: string; body: TransferTicketBody; fromMemberId?: string }) =>
            transferTicket({ centerId, body }),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ticketTransferKeys.all });
            queryClient.invalidateQueries({ queryKey: groupTicketKeys.all });
            queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] });
            // 서버가 받는 회원을 (원본 또는 선택한) 강사와 자동 연결하므로 담당 강사 목록도 갱신한다.
            queryClient.invalidateQueries({ queryKey: ['memberCoach'] });
            queryClient.invalidateQueries({ queryKey: publicMemberKeys.list(variables.centerId) });
            // 받는 회원 + 보내는(원본) 회원 상세 모두 무효화
            queryClient.invalidateQueries({ queryKey: publicMemberKeys.detail(variables.centerId, variables.body.toMemberId) });
            if (variables.fromMemberId) {
                queryClient.invalidateQueries({ queryKey: publicMemberKeys.detail(variables.centerId, variables.fromMemberId) });
            }
        },
        onError: (_error, variables) => {
            // 실패 시 옵션 캐시를 무효화 → stale 잔여회차로 같은 값 재시도가 반복 실패하는 것을 방지
            queryClient.invalidateQueries({ queryKey: ticketTransferKeys.all });
            // 응답 유실이면 처리됐을 수도 있다. 관리자가 잔여를 확인할 수 있게 수강권·회원 상세도 다시 불러온다.
            queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] });
            if (variables?.fromMemberId) {
                queryClient.invalidateQueries({ queryKey: publicMemberKeys.detail(variables.centerId, variables.fromMemberId) });
            }
            // 서버가 강사를 거부한 경우(퇴사·센터 이동) 센터 강사 목록도 다시 받아 드롭다운이
            // 실제 상태를 보여 주게 한다. 문구가 "강사 목록을 새로고침한 뒤" 라고 안내한다.
            queryClient.invalidateQueries({ queryKey: ['getCoachListForCenterQuery'] });
        },
    });
};

